import React, { useEffect, useState, useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useFetchData } from 'services/queries';

// Create filter options that search both name and abbreviation
const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) => `${option.name} ${option.abbreviation}`,
  limit: 50
});

// Special "Other" option for manual entry
const OTHER_OPTION = {
  id: null,
  name: 'Other - Manual Entry',
  abbreviation: 'OTHER',
  isOther: true
};

const PaymentTermsAutocomplete = ({
  value,
  onChange,
  error,
  helperText,
  required = false,
  disabled = false,
  options
}) => {
  const [resolvedValue, setResolvedValue] = useState(null);

  // Fetch payment term templates from API if not provided
  const { data: fetchedPaymentTerms } = useFetchData(
    'payment-terms/templates/active',  // Remove trailing slash for consistency
    true // Always fetch to resolve IDs
  );

  // Add "Other" option to the list
  const paymentTerms = useMemo(() => {
    const baseTerms = options || fetchedPaymentTerms || [];
    return [...baseTerms, OTHER_OPTION];
  }, [options, fetchedPaymentTerms]);

  // Resolve ID to full object when value is just an ID
  useEffect(() => {
    if (value && paymentTerms && paymentTerms.length > 0) {
      // Check if value is the "Other" option
      if (value === null || (typeof value === 'object' && value.isOther)) {
        setResolvedValue(OTHER_OPTION);
      } else if (typeof value === 'number' || (typeof value === 'object' && !value.name)) {
        // Value is an ID or incomplete object, find the full object
        const valueId = typeof value === 'number' ? value : value.id;
        const fullObject = paymentTerms.find(pt => pt.id === valueId);
        if (fullObject) {
          setResolvedValue(fullObject);
        }
      } else {
        // Value is already a full object
        setResolvedValue(value);
      }
    } else {
      setResolvedValue(null);
    }
  }, [value, paymentTerms]);

  return (
    <Autocomplete
      value={resolvedValue}
      onChange={(_, newValue) => onChange(newValue)}
      options={paymentTerms || []}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option?.name || ''}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {option.abbreviation}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.name}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Payment Terms Template"
          placeholder="Type abbreviation or description..."
          error={error}
          helperText={helperText}
          required={required}
        />
      )}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      disabled={disabled}
      fullWidth
      sx={{
        '& .MuiAutocomplete-option': {
          padding: '8px 16px',
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': {
            borderBottom: 'none'
          }
        }
      }}
    />
  );
};

export { OTHER_OPTION };
export default PaymentTermsAutocomplete;